import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Trash2,
  MapPin, ExternalLink, Package, X, Calculator,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  DESIGN_COAT_PRODUCTS,
  PIGMENTS_PRODUCTS,
  TOP_COAT_PRODUCTS,
} from '@/components/proepoxy/productData';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function MetallicStep1({ state, priority, setPriority, currentStep, goToStep, navigate, onExit }) {
  const {
    primerAreas, setPrimerAreas, designCoatSelections, pigmentSelections, topCoatSelections,
    steps,
    addPrimerArea, removePrimerArea, updatePrimerArea, updateAreaPackQty,
    updateAreaField, updateAreaFloorPrepQty,
    initAreaTopCoat, updateAreaTopCoatFinish, updateAreaTopCoatPackQty,
    getAreaTopCoatSubtotal, getAreaCoverage, getAreaSubtotal,
    initializeDesignCoatForArea, updateDesignCoatProduct,
    updateDesignCoatPackQtyLegacy, updateCrystalUnitsLegacy,
    getDesignCoatSubtotal, getDesignCoatCoverage,
    selectTopCoatProduct, updateTopCoatFinishLegacy, updateTopCoatPackQtyLegacy,
    getTopCoatSubtotal, getTopCoatCoverage,
    addMetallicPowder, updateMetallicPowderQty,
    addPigmentPaste, removePigmentPaste, updatePigmentPastePackQty,
    addGlitter, removeGlitter, updateGlitterPackQty,
    addSpray, updateSprayQty, getPigmentsSubtotal,
    calculateTotal, getItemCount,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-[#27292E] mb-2">Define Your Areas</h2>
                  <p className="text-gray-600 mb-6">
                    Add the areas you&apos;ll be working on. Each area can have different products and specifications.
                  </p>

                  {primerAreas.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                      <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 mb-4">No areas added yet</p>
                      <Button
                        onClick={addPrimerArea}
                        className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white"
                        data-testid="add-first-area-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Area
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {primerAreas.map((area, index) => (
                        <div
                          key={area.id}
                          className="border-2 border-gray-200 rounded-xl p-4 hover:border-[#7A0B7A]/30 transition-colors"
                          data-testid={`area-${index}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Area Name</label>
                                <Input
                                  type="text"
                                  value={area.name}
                                  onChange={(e) => updatePrimerArea(area.id, 'name', e.target.value)}
                                  className="border-2 rounded-lg"
                                  placeholder="e.g., Kitchen, Lounge"
                                  data-testid={`area-name-${index}`}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Size (m²)</label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={area.m2}
                                  onChange={(e) => updatePrimerArea(area.id, 'm2', e.target.value)}
                                  className="border-2 rounded-lg"
                                  placeholder="0"
                                  data-testid={`area-m2-${index}`}
                                />
                              </div>
                            </div>
                            <button
                              onClick={() => removePrimerArea(area.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove area"
                              data-testid={`remove-area-${index}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        onClick={addPrimerArea}
                        className="w-full py-4 border-2 border-dashed border-[#7A0B7A]/30 rounded-xl text-[#7A0B7A] font-medium hover:bg-[#7A0B7A]/5 hover:border-[#7A0B7A] transition-colors flex items-center justify-center gap-2"
                        data-testid="add-another-area-btn"
                      >
                        <Plus className="w-5 h-5" />
                        Add Another Area
                      </button>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => onExit()}
                    variant="outline"
                    className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(2)}
                    disabled={primerAreas.length === 0 || primerAreas.some(a => !a.m2)}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl disabled:opacity-50"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
